import { useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router";

import AuthCatch from "@/app/auth/AuthCatch";
import ConfirmAccount from "@/app/auth/ConfirmAccount";
import ConfirmEmail from "@/app/auth/ConfirmEmail";
import Login from "@/app/auth/Login";
import PasswordForgotten from "@/app/auth/PasswordForgotten";
import ResetPassword from "@/app/auth/ResetPassword";
import Accounts from "@/app/backstage/accounts/Accounts";
import Backstage from "@/app/backstage/backstage/Backstage";
import Devices from "@/app/backstage/devices/Devices";
import MyPartner from "@/app/backstage/mypartner/MyPartner";
import Partners from "@/app/backstage/partners/Partners";
import Pbxes from "@/app/backstage/pbxes/Pbxes";
import PhoneNumbers from "@/app/backstage/phonenumbers/PhoneNumbers";
import CallParkings from "@/app/domain/callflow/callparkings/CallParkings";
import Conferences from "@/app/domain/callflow/conferences/Conferences";
import DayNights from "@/app/domain/callflow/daynight/DayNights";
import CustomDestinations from "@/app/domain/callflow/destinations/Destinations";
import Groups from "@/app/domain/callflow/groups/Groups";
import InboundRoutes from "@/app/domain/callflow/inbounds/Inbounds";
import Intercoms from "@/app/domain/callflow/intercoms/Intercoms";
import Ivrs from "@/app/domain/callflow/ivrs/Ivrs";
import OutboundRules from "@/app/domain/callflow/outbounds/Outbounds";
import Queues from "@/app/domain/callflow/queues/Queues";
import HolidaySets from "@/app/domain/callflow/timetables/holidaysets/HolidaySets";
import Timetable from "@/app/domain/callflow/timetables/timetable/Timetable";
import Timetables from "@/app/domain/callflow/timetables/Timetables";
import Users from "@/app/domain/callflow/users/Users";
import Voicemails from "@/app/domain/callflow/voicemails/Voicemails";
import Dashboard from "@/app/domain/dashboard/Dashboard";
import LogCalls from "@/app/domain/logs/calls/CallLog";
import LogFlexDesks from "@/app/domain/logs/flexdesks/FlexDeskLog";
import LogQueueMembers from "@/app/domain/logs/queuemembers/QueueMemberLog";
import LogQueues from "@/app/domain/logs/queues/QueueLog";
import StatsQueues from "@/app/domain/logs/queuestats/QueueStats";
import LogWebhooks from "@/app/domain/logs/webhooks/WebhookLog";
import Library from "@/app/domain/media/library/Library";
import MusicOnHolds from "@/app/domain/media/musiconhold/MusicOnHolds";
import PbxOverview from "@/app/domain/pbx/Overview";
import PhonebookContactGroup from "@/app/domain/phonebooks/ContactGroup";
import Phonebook from "@/app/domain/phonebooks/Phonebook";
import Phonebooks from "@/app/domain/phonebooks/Phonebooks";
import Provisioning from "@/app/domain/provisioning/Provisionings";
import Blocklists from "@/app/domain/settings/blocklists/Blocklists";
import DistinctiveRingtones from "@/app/domain/settings/distinctiveringtones/DistinctiveRingtones";
import LineKeyTemplates from "@/app/domain/settings/linekeytemplates/LineKeyTemplates";
import Webhooks from "@/app/domain/settings/webhooks/Webhooks";
import useMatomo from "@/utils/hooks/useMatomo";

import MobileSippeerSettings from "./domain/callflow/mobileSippeers/MobileSippeerSettings";
import MobileSippeersLayout from "./domain/callflow/mobileSippeers/MobileSippeersLayout";
import MobileSippeerStart from "./domain/callflow/mobileSippeers/MobileSippeerStart";
import PbxContainer from "./PbxContainer";
import Wrapper from "./Wrapper";

const Router = () => {
  useMatomo();

  const auth = useSelector((state) => state.auth);

  if (auth.id) {
    return (
      <Routes>
        <Route path="*" element={<Wrapper />}>
          <Route path="backstage">
            <Route path="*" element={<Navigate to="../home" replace />} />
            <Route path="home" element={<Backstage />} />
            {auth?.accessLevel <= 1 && (
              <>
                <Route path="pbxes">
                  <Route path="*" element={<Pbxes />} />
                </Route>
                <Route path="accounts">
                  <Route path="*" element={<Accounts />} />
                </Route>
                <Route path="phone-numbers/:did?" element={<PhoneNumbers />} />
                <Route path="devices">
                  <Route path="*" element={<Devices />} />
                </Route>
              </>
            )}
            {auth?.accessLevel === 1 && <Route path="my-partner" element={<MyPartner />} />}
            {auth?.accessLevel === 0 && (
              <Route path="partners">
                <Route path="*" element={<Partners />} />
              </Route>
            )}
          </Route>

          <Route path="pbx/:pbxId" element={<PbxContainer />}>
            <Route path="*" element={<Navigate to="../dashboard" replace />} />

            <Route path="dashboard" element={<Dashboard />} />

            <Route path="logs">
              <Route index element={<Navigate to="calls" replace />} />
              <Route path="calls" element={<LogCalls />} />
              <Route path="queues" element={<LogQueues />} />
              <Route path="queue-stats" element={<StatsQueues />} />
              <Route path="queue-members" element={<LogQueueMembers />} />
              <Route path="flex-desks" element={<LogFlexDesks />} />
              {auth.accessLevel <= 3 && <Route path="webhooks" element={<LogWebhooks />} />}
            </Route>

            {auth.accessLevel <= 3 && (
              <>
                <Route path="phonebooks">
                  <Route path=":id/:groupId" element={<PhonebookContactGroup />} />
                  <Route path=":id" element={<Phonebook />} />
                  <Route index element={<Phonebooks />} />
                </Route>

                <Route path="media">
                  <Route index element={<Navigate to="library" replace />} />
                  <Route path="library" element={<Library />} />
                  <Route path="library/:type/:id" element={<Library />} />
                  <Route path="music-on-hold">
                    <Route path="*" element={<MusicOnHolds />} />
                  </Route>
                </Route>

                <Route path="call-flow">
                  <Route index element={<Navigate to="users" replace />} />
                  <Route path="users">
                    <Route path="*" element={<Users />} />
                  </Route>
                  <Route path="mobile-users" Component={MobileSippeersLayout}>
                    <Route index element={<MobileSippeerStart />} />
                    <Route path=":mobileSippeerId" element={<MobileSippeerSettings />} />
                  </Route>
                  <Route path="groups">
                    <Route path="*" element={<Groups />} />
                  </Route>
                  <Route path="inbound-routes">
                    <Route path="*" element={<InboundRoutes />} />
                  </Route>
                  <Route path="intercoms">
                    <Route path="*" element={<Intercoms />} />
                  </Route>
                  <Route path="queues">
                    <Route path="*" element={<Queues />} />
                  </Route>
                  <Route path="voicemails">
                    <Route path="*" element={<Voicemails />} />
                  </Route>
                  <Route path="custom-destinations">
                    <Route path="*" element={<CustomDestinations />} />
                  </Route>
                  <Route path="ivrs">
                    <Route path="*" element={<Ivrs />} />
                  </Route>
                  <Route path="outbound-rules">
                    <Route path="*" element={<OutboundRules />} />
                  </Route>
                  <Route path="timetables" element={<Timetables />} />
                  <Route path="timetable" element={<Timetable />} />
                  <Route path="timetable/:id" element={<Timetable />} />
                  <Route path="holidaysets">
                    <Route path="*" element={<HolidaySets />} />
                  </Route>
                  <Route path="conferences">
                    <Route path="*" element={<Conferences />} />
                  </Route>
                  <Route path="call-parkings">
                    <Route path="*" element={<CallParkings />} />
                  </Route>
                  <Route path="switches">
                    <Route path="*" element={<DayNights />} />
                  </Route>
                </Route>

                <Route path="settings">
                  <Route index element={<Navigate to="line-key-templates" replace />} />
                  <Route path="line-key-templates">
                    <Route path="*" element={<LineKeyTemplates />} />
                  </Route>
                  <Route path="distinctive-ringtones">
                    <Route path="*" element={<DistinctiveRingtones />} />
                  </Route>
                  <Route path="webhooks">
                    <Route path="*" element={<Webhooks />} />
                  </Route>
                  <Route path="blocklists">
                    <Route path="*" element={<Blocklists />} />
                  </Route>
                </Route>

                <Route path="provisioning">
                  <Route path="*" element={<Provisioning />} />
                </Route>

                <Route path="my-pbx">
                  <Route path="*" element={<PbxOverview />} />
                </Route>
              </>
            )}
          </Route>

          <Route path="*" element={<Navigate to="/backstage/home" replace />} />
        </Route>
        <Route path="confirm-email" element={<ConfirmEmail />} />
        <Route path="login" element={<AuthCatch message="You cannot log in again." />} />
        <Route
          path="confirm-user"
          element={<AuthCatch message="You cannot confirm an account while you are logged in." />}
        />
        <Route
          path="password-forgotten"
          element={<AuthCatch message="You cannot reset your password while you are logged in." />}
        />
        <Route
          path="reset-password"
          element={<AuthCatch message="You cannot reset your password while you are logged in." />}
        />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="*" element={<Login />} />
      <Route path="confirm-email" element={<ConfirmEmail />} />
      <Route path="confirm-user" element={<ConfirmAccount />} />
      <Route path="password-forgotten" element={<PasswordForgotten />} />
      <Route path="reset-password" element={<ResetPassword />} />
    </Routes>
  );
};

export default Router;
